import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Period } from 'dashboard/lib/unixTime';
import { getEffectivePeriod } from 'dashboard/lib/utils/period';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import type { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { MetricsGroupBy } from 'dashboard/models/metrics';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  clearMetrics,
  loadQueryTemplate,
  MetricsFilterParams,
  resetFiltersToInitialState,
  resetMetricsDateFilter,
  setMetricsFilters,
  unsetSelectedTemplate,
} from 'dashboard/redux/actions/metrics-inquiry';
import { getInquiryAnalyticsQueryTemplatesSelector } from 'dashboard/redux/selectors/analytics-query-templates';
import {
  getAdjustedFilters,
  getDynamicFilters,
  getIsSelectedQueryTemplateDifferentFromFilters,
} from 'dashboard/redux/selectors/metrics-inquiry';
import { getAllTemplates, getInquiryTags } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import { getAnalyticsQueryTemplates } from 'dashboard/redux/thunk-actions/analytics-query-templates';
import {
  loadExporterMetricsInquiry,
  loadMetricsInquiryDynamicFilters,
  loadMetricsInquiryVolumeOverTime,
  setGranularities,
} from 'dashboard/redux/thunk-actions/metrics-inquiry';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardInquiryAnalyticsVolumeV2View from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View';
import { CountryCode } from 'lib/constants/locales';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const findSelectedTemplates = (
  filters: MetricsFilterParams,
  templates: (Template | InquiryTemplate)[],
): (Template | InquiryTemplate)[] => {
  if (filters.templateOrInquiryTemplateIds == null) {
    return [];
  }
  return templates.filter((t) => filters.templateOrInquiryTemplateIds.includes(t.id));
};

export const findSelectedTags = (filters: MetricsFilterParams, tags: Tag[]): Tag[] => {
  if (filters.inquiryTagIds == null) {
    return [];
  }
  return tags.filter((t) => filters.inquiryTagIds.includes(t.id));
};

const DashboardInquiryAnalyticsVolumeRoute = (
  props: PropsFromRedux & {
    loadVolumeMetricsV2: (inquiryTemplateIds: string[]) => Promise<void>;
    openCreateQueryTemplateModal: () => void;
    loading: boolean;
    error: boolean;
  },
) => {
  const {
    filters,
    templates,
    lastQueryFilters,
    inquiryTags,
    loadVolumeMetricsV2,
    granularities,
    error,
    loading,
    topLevelFiltersChanged,
    resetFiltersToInitialState,
    openCreateQueryTemplateModal,
    queryTemplates,
    selectedQueryTemplate,
    isQueryTemplateModified,
    currentEnvironment,
    unsetSelectedTemplate,
  } = props;

  useEffectOnEnvironmentChange(() => {
    props.clearMetrics();
  });

  // Load in our Inquiry Tags
  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Inquiry),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: loadingGranular,
    callback: handleUpdateGranularity,
    error: errorGranular,
  } = useApiCallback(
    async (period: Period) => {
      const overrideFilters = {
        ...filters,
        templateOrInquiryTemplateIds:
          selectedTemplates == null ? [] : selectedTemplates.map((template) => template.id),
      };
      const effectivePeriod = getEffectivePeriod(period, [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);
      await Promise.all([
        props.setGranularities({
          granularities: {
            ...granularities,
            overview: effectivePeriod,
          },
        }),
        props.loadMetricsInquiryVolumeOverTime({
          currentEnvironment,
          filters: overrideFilters,
          overviewPeriod: effectivePeriod,
        }),
      ]);
    },

    [currentEnvironment, filters, granularities],
  );

  useApiErrorHandler({
    error: errorGranular,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiry metrics.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { loading: loadingDynamicFilters } = useApiLoader({
    loader: async () => {
      await props.loadMetricsInquiryDynamicFilters({
        currentEnvironment: currentEnvironment,
        filters,
      });
    },
    deps: [filters, currentEnvironment, props.loadMetricsInquiryDynamicFilters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load dynamic filters.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    skip: !topLevelFiltersChanged,
  });

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsFilters,
  });

  const handleGroupByFilterUpdateForToggle = useCallback((checked: boolean) => {
    props.setMetricsFilters({ groupBy: checked ? MetricsGroupBy.Accounts : undefined });
  }, []);

  const selectedTemplates = useMemo(
    () => findSelectedTemplates(filters, templates),
    [filters, templates],
  );

  const selectedTags = useMemo(
    () => findSelectedTags(filters, inquiryTags),
    [filters, inquiryTags],
  );

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      // Ensure we have a date range before we extract dates
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({ startDate, endDate })
          : props.resetMetricsDateFilter();
      }
    },

    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  const handleInquiryTemplateFilterUpdate = useCallback((inquiryTemplateIds: string[]) => {
    props.setMetricsFilters({
      templateOrInquiryTemplateIds: inquiryTemplateIds,
    });
  }, []);

  const handleTagFilterUpdate = useCallback((tagIds: string[]) => {
    props.setMetricsFilters({
      inquiryTagIds: tagIds,
    });
  }, []);

  const handleCountryFilterUpdate = useCallback((countryCodes: CountryCode[]) => {
    props.setMetricsFilters({
      countryCodes: countryCodes,
    });
  }, []);

  const handlePlatformFilterUpdate = useCallback((platforms: string[]) => {
    props.setMetricsFilters({
      platforms: platforms,
    });
  }, []);

  const handleDeviceFilterUpdate = useCallback((deviceGroups: string[]) => {
    props.setMetricsFilters({
      deviceGroups: deviceGroups,
    });
  }, []);

  const resetToInitialStateAndLoad = useCallback(
    () => resetFiltersToInitialState(),
    [resetFiltersToInitialState],
  );

  const handleLoadQueryTemplate = useCallback((template: AnalyticsQueryTemplate) => {
    props.loadQueryTemplate(template);
  }, []);

  return (
    <DashboardInquiryAnalyticsVolumeV2View
      filters={filters}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleTemplateFilterUpdate={handleInquiryTemplateFilterUpdate}
      handleTagFilterUpdate={handleTagFilterUpdate}
      loading={loading || loadingTags || loadingGranular}
      errored={error}
      loadVolumeMetrics={loadVolumeMetricsV2}
      volume={props.volume}
      volumeDelta={props.volumeDelta}
      volumeOverTime={props.volumeOverTime}
      medianCompletion={props.medianCompletion}
      medianCompletionDelta={props.medianCompletionDelta}
      queriedAt={props.queriedAt}
      templates={props.templates}
      selectedTemplates={selectedTemplates}
      selectedTags={selectedTags}
      handleGroupByFilterUpdateForToggle={handleGroupByFilterUpdateForToggle}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCountryFilterUpdate={handleCountryFilterUpdate}
      handleDeviceFilterUpdate={handleDeviceFilterUpdate}
      handlePlatformFilterUpdate={handlePlatformFilterUpdate}
      handleUpdateGranularity={handleUpdateGranularity}
      overviewPeriod={props.granularities.overview}
      lastQueryFilters={lastQueryFilters.overview}
      inquiryTags={inquiryTags}
      dynamicFilters={props.dynamicFilters}
      loadingDynamicFilters={loadingDynamicFilters}
      resetFiltersToInitialState={resetToInitialStateAndLoad}
      openCreateQueryTemplateModal={openCreateQueryTemplateModal}
      queryTemplates={queryTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedQueryTemplate={selectedQueryTemplate}
      handleLoadQueryTemplate={handleLoadQueryTemplate}
      isQueryTemplateModified={isQueryTemplateModified}
      unsetSelectedTemplate={unsetSelectedTemplate}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  volumeSummary: state.metricsInquiry.volumeSummary,
  volume: state.metricsInquiry.volume,
  volumeOverTime: state.metricsInquiry.volumeOverTime,
  medianCompletion: state.metricsInquiry.medianCompletion,
  queriedAt: state.metricsInquiry.queriedAt,
  filters: getAdjustedFilters(state),
  lastQueryFilters: state.metricsInquiry.lastQueryFilters,
  templates: getAllTemplates(state),
  inquiryTags: getInquiryTags(state),
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  dynamicFilters: getDynamicFilters(state),
  topLevelFiltersChanged: state.metricsInquiry.topLevelFiltersChanged,
  volumeDelta: state.metricsInquiry.volumeDelta,
  medianCompletionDelta: state.metricsInquiry.medianCompletionDelta,
  queryTemplates: getInquiryAnalyticsQueryTemplatesSelector(state),
  selectedQueryTemplate: state.metricsInquiry.selectedQueryTemplate.template,
  isQueryTemplateModified: getIsSelectedQueryTemplateDifferentFromFilters(state),
  granularities: state.metricsInquiry.granularities,
});

const mapDispatchToProps = {
  clearMetrics,
  setMetricsFilters,
  resetMetricsDateFilter,
  setGranularities,
  loadMetricsInquiryVolumeOverTime,
  showMessage,
  loadExporterMetricsInquiry,
  loadTags,
  loadMetricsInquiryDynamicFilters,
  resetFiltersToInitialState,
  getAnalyticsQueryTemplates,
  loadQueryTemplate,
  unsetSelectedTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryAnalyticsVolumeRoute);
